import { motion } from 'framer-motion';
import React from 'react';
import { styled } from 'styled-components';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { usePanning } from '@fabric-app/ui/hooks/usePanning';
import {
  DragHandle,
  BottomBar,
} from '@fabric-app/business/resource-detail/components/ExpandedResource/MobileView/ResizableBottomBar';
import { SearchResultsFabricItems } from './SearchResultsFabricItems';
import type { SearchResultProps } from './types';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { SearchResultsExternalItems } from './SearchResultsExternalItems';
import { useQueryResourceContextSearchExternal } from '@fabric-app/business/resources/queries/useQueryResourceContextSearchExternal';
import { Flex } from '@fabric-app/ui/components/Flex';
import { IconNavigational24Connections, IconNavigational24HomeIcon } from '@fabric-app/icons/svg';
import { useQueryIntegrations } from '@fabric-app/business/connections/queries/useQueryIntegrations';
import { useVirtualKeyboardContext } from '@fabric-app/business/keyboard/keyboard.context';
import { useSearchOverlay } from '@fabric-app/business/search/hooks/useSearchOverlay';

const BASE_BOTTOM_BAR_HEIGHT = `env(safe-area-inset-bottom) + 47px`;

const Content = styled.div`
  height: 1px;
  flex-grow: 1;
  background: ${cssVar['color-bg-primary']};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  border-radius: 0 0 1.5rem 1.5rem;
  transition: 0.35s max-height ease-out;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background: black;

  /** when keyboard is opened, e.g. notepad, we shirnk the content */
  height: 100%;
  padding-bottom: var(--keyboard-height, 0);
`;

const getMaxBottomHeight = () => window.innerHeight - 182;

const SearchResultsMobileBase: React.FC<SearchResultProps> = (props) => {
  const { queryArgs, total, queryHash } = props;
  const searchedText = 'text' in queryArgs ? queryArgs.text : '';
  const { hideExternalSearch } = useSearchOverlay();
  const { integrations } = useQueryIntegrations();
  const hasSearchBasedIntegrations = integrations.some(
    (integration) => integration.viewConfig.isSearchBased,
  );

  const showExternalSearch = hasSearchBasedIntegrations && !!searchedText && !hideExternalSearch;

  const containerRef = React.useRef<HTMLDivElement>(null);
  /*********************************************************************************
   * resizing
   */
  const [bottomAdditionalHeight, setBottomAdditionalHeight] = React.useState(0);

  const { motionProps, isPanningY } = usePanning({
    panningLayerId: 'search-results-drag-resize',
    onPan: (_e, info, moreInfo) => {
      if (moreInfo.axis === 'y') {
        setBottomAdditionalHeight((prev) => prev - info.delta.y);
      }
    },
    onPanEnd: (_e, { velocity }, { axis }) => {
      if (axis !== 'y') {
        return;
      }
      /**
       * swipe down fast, collapse completely
       */
      if (velocity.y > 150) {
        setBottomAdditionalHeight(0);
        return;
      }
      /**
       * swipe up fast, expanded fully
       */
      if (velocity.y < -150) {
        setBottomAdditionalHeight(getMaxBottomHeight());
        return;
      }
      /**
       * if size is not big enough, snap to bottom
       */
      setBottomAdditionalHeight((prev) => (prev < 100 ? 0 : prev));
    },
  });

  const { height: keyboardHeight } = useVirtualKeyboardContext();

  const bottomHeight =
    keyboardHeight === 0
      ? `calc(${bottomAdditionalHeight}px + ${BASE_BOTTOM_BAR_HEIGHT})`
      : `calc(${bottomAdditionalHeight}px)`;
  const {
    resources: externalResources,
    total: externalTotal,
    isLoading: isLoadingExternal,
  } = useQueryResourceContextSearchExternal(
    {
      query: searchedText,
    },
    {
      enabled: !!searchedText && showExternalSearch,
    },
  );

  const toggleAdditionalHeight = () => {
    setBottomAdditionalHeight((value) => (value > 100 ? 0 : getMaxBottomHeight()));
  };

  if (!showExternalSearch) {
    return (
      <>
        <ResultHeader>
          <IconNavigational24HomeIcon />
          <ResultHeaderText>{total} results from your Fabric</ResultHeaderText>
        </ResultHeader>
        <ScrollArea.Default scrollbarVariant="none" key={queryHash}>
          <SearchResultsFabricItems {...props} />
        </ScrollArea.Default>
      </>
    );
  }

  return (
    <Wrapper as={motion.div} ref={containerRef}>
      <Content
        as={motion.div}
        transition={{
          // for the height change
          duration: 0.03,
        }}
      >
        <ResultHeader {...motionProps} onClick={toggleAdditionalHeight}>
          <IconNavigational24HomeIcon />
          <ResultHeaderText>{total} results from your Fabric</ResultHeaderText>
        </ResultHeader>
        <ScrollArea.Default scrollbarVariant="none" key={queryHash}>
          <SearchResultsFabricItems {...props} />
        </ScrollArea.Default>
      </Content>
      <StyledDragHandle
        key="drag-handle"
        isActive={isPanningY}
        style={{ pointerEvents: 'auto' }}
        {...motionProps}
      />
      <StyledBottomBar
        style={{
          background: cssVar['color-bg-primary'],
          minHeight: keyboardHeight === 0 ? BASE_BOTTOM_BAR_HEIGHT : 47,
        }}
        animate={{
          height: bottomHeight,
          transition: {
            duration: isPanningY ? 0.0 : 0.15,
            ease: 'linear',
          },
        }}
      >
        <Flex flexGrow={1} direction="column" style={{ overflow: 'hidden', height: '100%' }}>
          <ResultHeader {...motionProps} onClick={toggleAdditionalHeight}>
            <IconNavigational24Connections />
            <ResultHeaderText>{externalTotal} results from connected sources</ResultHeaderText>
          </ResultHeader>
          <ScrollArea.Default scrollbarVariant="none" key={`${queryHash}-external`}>
            <SearchResultsExternalItems
              {...props}
              isLoading={isLoadingExternal}
              resources={externalResources}
              total={externalTotal}
            />
          </ScrollArea.Default>
        </Flex>
      </StyledBottomBar>
    </Wrapper>
  );
};

const ResultHeader = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  height: 48px;
  flex-shrink: 0;
  border-bottom: 1px solid ${cssVar['color-border-primary']};
  color: ${cssVar['color-text-tertiary']};
  touch-action: pinch-zoom;
`;

const ResultHeaderText = styled.div`
  font-size: 1rem;
  font-weight: 600;
`;

const StyledBottomBar = styled(BottomBar)`
  max-height: calc(100dvh - var(--keyboard-height, 0px) - env(safe-area-inset-top) - 60px - 78px);
`;

const StyledDragHandle = styled(DragHandle)`
  &:after {
    height: 50px;
  }
`;

export const SearchResultsMobile = React.memo(SearchResultsMobileBase);
